import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import { IFinalEvent } from "types/event"
import { sortPrice } from "utils/helpers/sortEventPrice"
import PriceComponent from "components/atoms/PriceComponent"
import Link from "next/link"
import { hasMultiplePrices } from "utils/helpers/eventPrice"
import { twMerge } from "tailwind-merge"
import Button from "components/Form/Button"
import { useEffect, useState, useCallback } from "react"
import useWindowSize from "hooks/useWindowSize"
import { useRouter } from "next/router"
import { useCartDispatch } from "hooks/useCartV2"
import { useTheme } from "next-themes"
import useVerificationRequired from "hooks/useVerificationRequired"

interface IProps {
    event: IFinalEvent
    className?: string
    verificationModalOpen: boolean
    setOpenVerificationModal: (value: boolean) => void
    isExpired?: boolean | undefined
}

const F1ActionCard = ({
    event,
    className: customClassName,
    setOpenVerificationModal,
    verificationModalOpen,
}: IProps) => {
    const { token } = useUserContext()
    const { t, isAr } = useLang()
    const { isUserVerified, isVerificationRequired } = useVerificationRequired()
    const { isDesktop } = useWindowSize()
    const [showActionCard, setShowActionCard] = useState(false)
    const router = useRouter()
    const cartDispatch = useCartDispatch()
    const { theme } = useTheme()

    const handleScroll = useCallback(() => {
        const scrollPosition = window.scrollY
        const threshold = isDesktop ? 200 : 0
        const footer = document.querySelector("footer")

        if (!footer) {
            setShowActionCard(scrollPosition > threshold)
            return
        }

        const footerPosition =
            footer.getBoundingClientRect().top + window.scrollY
        const viewportHeight = window.innerHeight
        const buffer = 150 // Buffer before footer

        const shouldShow =
            scrollPosition > threshold &&
            scrollPosition < footerPosition - viewportHeight - buffer

        setShowActionCard(shouldShow)
    }, [isDesktop])

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null

        const throttledScroll = () => {
            if (timeoutId) return

            timeoutId = setTimeout(() => {
                handleScroll()
                timeoutId = null
            }, 100) // Throttle to 100ms
        }

        window.addEventListener("scroll", throttledScroll)
        handleScroll() // Initial check

        return () => {
            window.removeEventListener("scroll", throttledScroll)
            if (timeoutId) clearTimeout(timeoutId)
        }
    }, [handleScroll])

    const price = sortPrice(event)

    // Split the asPath into pathname and query string
    const [pathWithoutQuery, queryString] = router.asPath.split("?")

    // Construct the new path by adding '/book' before the query parameters
    const newPath = `${pathWithoutQuery}/book`

    // Reconstruct the href, including query parameters if they exist
    const href = queryString ? `${newPath}?${queryString}` : newPath

    const handleLinkClick = (e: React.MouseEvent) => {
        const clearCartAndSetTitle = () => {
            cartDispatch({ type: "CLEAR_CART" })
            cartDispatch({ type: "SET_EVENT_TITLE", payload: event?.ar_title })
        }

        const navigateToBookingPage = () => {
            clearCartAndSetTitle()
        }

        if (!token) {
            // User is not logged in
            clearCartAndSetTitle()
            navigateToBookingPage()
        } else {
            // User is logged in
            if (!isVerificationRequired || isUserVerified) {
                clearCartAndSetTitle()
                navigateToBookingPage()
            } else {
                e.preventDefault() // Prevent navigation
                setOpenVerificationModal(true) // Open verification modal
            }
        }
    }

    function everyFunc(el: { end_slot: string | number | Date }) {
        const today = new Date()
        if (today > new Date(el.end_slot)) {
            return true
        } else {
            return false
        }
    }

    const isTimeSlotsExpired = event?.time_slots?.every(everyFunc)

    // Check if event is expired based on ending date
    const isEventExpired = (() => {
        const today = new Date()
        const eventEndDate = event?.event_date?.ending_date
            ? new Date(event.event_date.ending_date)
            : null
        return eventEndDate ? today > eventEndDate : false
    })()

    const isBookingDisabled = () => {
        const hasNoAvailableTickets = event?.availableTickets?.length === 0
        const hasNoTimeSlots = event?.time_slots?.length === 0
        const hasExpiredTimeSlots = !isEventExpired && isTimeSlotsExpired

        return hasNoAvailableTickets || hasNoTimeSlots || hasExpiredTimeSlots
    }

    const isButtonDisabled = isBookingDisabled()

    const isMultiplePrices = hasMultiplePrices(event)
    const isBookingEnabled = event?.booking?.is_enable_booking !== false
    const getButtonLabel = () => {
        if (isBookingEnabled) {
            return t("book_now")
        }
        if (isAr) {
            return event.booking?.ar_message ?? t("not_available")
        }
        return event.booking?.en_message ?? t("not_available")
    }

    const renderActionButton = () => {
        if (isEventExpired) {
            return (
                <Button disabled className="w-full p-2">
                    {t("event_expired")}
                </Button>
            )
        }

        if (isButtonDisabled) {
            return (
                <Button disabled className="w-full p-2 capitalize">
                    {t("no_tickets_available")}
                </Button>
            )
        }

        return (
            <Link
                href={href}
                className="w-full"
                passHref
                onClick={handleLinkClick}
            >
                <Button
                    disabled={!isBookingEnabled}
                    className="w-full p-2"
                    onClick={handleLinkClick}
                >
                    {getButtonLabel()}
                </Button>
            </Link>
        )
    }

    return (
        <div
            className={twMerge(
                `${
                    verificationModalOpen
                        ? "block"
                        : showActionCard
                          ? "fixed z-[30]"
                          : "hidden"
                } 
                bottom-0 left-0 right-0 mx-auto 
                flex w-full max-w-[1200px] flex-col items-center justify-center 
                bg-opacity-50 px-5 gap-y-3 py-3
                border border-b-0 border-white border-opacity-20
                backdrop-blur-sm dark:bg-primary-theme-400 dark:bg-opacity-50 rounded-t-xl
                transition-all duration-300 ease-in-out transform translate-y-0
                opacity-100`,
                customClassName
            )}
        >
            {event?.availableTickets?.length === 0 ? null : (
                <div className="flex w-full flex-row items-center justify-between">
                    <span className="text-lg font-semibold text-primary-theme-400 dark:text-white">
                        {isMultiplePrices
                            ? t("starting_from")
                            : t("Ticket_price")}
                    </span>
                    {price?.price ? (
                        <PriceComponent
                            color={theme === "light" ? "#392986" : "white"}
                            className="flex flex-row items-center text-lg font-bold text-primary-theme-400 dark:text-white"
                            price={
                                price?.discount?.previous_value
                                    ? price?.discount?.current_value
                                    : price?.price
                            }
                        />
                    ) : null}
                </div>
            )}

            {renderActionButton()}
        </div>
    )
}

export default F1ActionCard
